import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useSettingsStore } from '../stores/settingsStore';
import { authService } from '../services/auth.service';
import { toSessionUser } from '../utils/sessionUser';

// VSNP-704: activity-based idle session timeout.
//
// Previously the frontend stamped a 20-minute absolute TTL on every auth
// storage entry and never reset it on user activity — so reviewing data
// without typing kicked you to /login at 20 minutes. The "Idle Timeout"
// setting in System Settings (default 15 min) was dead code.
//
// This hook installs a single global activity listener that resets a
// debounced timer. After N minutes of no user input, the session is
// revoked and the user is redirected to /login?reason=idle. A warning
// 60 seconds before expiry gives them a chance to stay logged in (any
// activity resets the timer, including dismissing the warning).
//
// Mounted from DashboardLayout so it only runs on authenticated routes.

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'wheel',
];

// Debounce activity events — without this we re-arm the timer 60 times
// per second on mousemove. 1s granularity is plenty for a multi-minute
// idle window.
const ACTIVITY_DEBOUNCE_MS = 1000;

// Warn the user N seconds before logout so they have time to click back
// in. Activity dismisses the warning automatically.
const WARNING_LEAD_SECONDS = 60;

// Floor: never let the configured value drop below 1 minute. The setting
// is operator-controlled but a misconfiguration of 0 would log everyone
// out instantly.
const MIN_IDLE_MINUTES = 1;

// Proactive backend refresh: when the user IS active, top up the VSN+
// session token before it expires. Backend refresh window is loose (any
// time inside REFRESH_THRESHOLD); we tick once a minute and let the
// server decide whether to rotate.
const REFRESH_TICK_MS = 60 * 1000;

export function useIdleTimeout() {
  const navigate = useNavigate();
  // VSNP-704 follow-up: the operator-facing field lives in the `security`
  // settings category. `general.sessionManagement.idleTimeoutMinutes` is a
  // separate DB row that the Security Settings tab does NOT write to —
  // reading from there meant we always saw the seed default (15) no
  // matter what the operator set. Prefer security, fall back to general
  // for tenants/installs where only general has been seeded, then 15.
  const idleMinutes = useSettingsStore(
    (s) =>
      s.security?.sessionManagement?.idleTimeoutMinutes ??
      s.general?.sessionManagement?.idleTimeoutMinutes ??
      15,
  );
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // VSNP-721: absolute session-lifetime cap. Backend pins this at session
  // creation (= login_at + min(env duration, settings.timeoutMinutes)) and
  // preserves it across refreshes. Null on remember-me sessions and on
  // tokens issued by a pre-VSNP-721 API — both safely skip the hard timer.
  const absoluteExpiresAt = useAuthStore((s) => s.vsnAbsoluteExpiresAt);

  // Refs so the event handlers don't re-bind on every state change.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const absoluteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);

  // Idle-listener effect. Deps are deliberately minimal — only the
  // settings that *change the meaning of idle* re-arm. Token rotation
  // (vsnTokenExpiresAt updates from /auth/refresh) MUST NOT reset the
  // idle timer; otherwise the proactive-refresh tick at minute 1
  // perpetually re-arms a 2-minute idle window and the user is never
  // logged out. The proactive refresh runs in its own effect below
  // and reads the latest token expiry via the store at tick time.
  useEffect(() => {
    if (!isAuthenticated) return;

    const idleMs = Math.max(MIN_IDLE_MINUTES, idleMinutes) * 60 * 1000;
    const warnMs = Math.max(0, idleMs - WARNING_LEAD_SECONDS * 1000);

    const showWarning = () => {
      if (warningShownRef.current) return;
      warningShownRef.current = true;
      window.dispatchEvent(
        new CustomEvent('idle-warning', {
          detail: { secondsRemaining: WARNING_LEAD_SECONDS },
        }),
      );
    };

    const fireLogout = () => {
      authService.logoutVSN().catch(() => undefined);
      useAuthStore.getState().logout();
      navigate('/login?reason=idle', { replace: true });
    };

    const resetTimers = () => {
      lastActivityRef.current = Date.now();
      if (warningShownRef.current) {
        warningShownRef.current = false;
        window.dispatchEvent(new CustomEvent('idle-warning-clear'));
      }
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      warnTimerRef.current = setTimeout(showWarning, warnMs);
      idleTimerRef.current = setTimeout(fireLogout, idleMs);
    };

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onActivity = () => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        resetTimers();
      }, ACTIVITY_DEBOUNCE_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') onActivity();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, onActivity, { passive: true }),
    );
    document.addEventListener('visibilitychange', onVisibility);

    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, onActivity),
      );
      document.removeEventListener('visibilitychange', onVisibility);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (debounceTimer) clearTimeout(debounceTimer);
      warningShownRef.current = false;
    };
  }, [isAuthenticated, idleMinutes, navigate]);

  // VSNP-721: absolute session-lifetime cap. Fires logout at
  // `absoluteExpiresAt` regardless of activity. Independent from the idle
  // timer — activity does NOT reset it. Backend enforces the same cap on
  // refresh (refusing to extend `exp` past `absoluteExp`), so the worst
  // case if this timer drifts is the next API call returns 401. Doing
  // the redirect here keeps the UX clean (operator lands on /login with a
  // banner rather than mid-page on a sudden 401).
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!absoluteExpiresAt) return; // remember-me or legacy session — no cap

    const msUntilAbsolute = new Date(absoluteExpiresAt).getTime() - Date.now();
    if (msUntilAbsolute <= 0) {
      // Already past the cap (e.g. tab woken from sleep after the limit).
      authService.logoutVSN().catch(() => undefined);
      useAuthStore.getState().logout();
      navigate('/login?reason=absolute', { replace: true });
      return;
    }

    absoluteTimerRef.current = setTimeout(() => {
      authService.logoutVSN().catch(() => undefined);
      useAuthStore.getState().logout();
      navigate('/login?reason=absolute', { replace: true });
    }, msUntilAbsolute);

    return () => {
      if (absoluteTimerRef.current) clearTimeout(absoluteTimerRef.current);
    };
  }, [isAuthenticated, absoluteExpiresAt, navigate]);

  // Proactive token-refresh effect. Independent from the idle timer so
  // token rotation doesn't perturb the idle countdown. Reads the latest
  // vsnTokenExpiresAt via useAuthStore.getState() at each tick.
  useEffect(() => {
    if (!isAuthenticated) return;

    refreshTimerRef.current = setInterval(() => {
      const expiresAt = useAuthStore.getState().vsnTokenExpiresAt;
      if (!expiresAt) return;
      const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
      // Only refresh if we expect the backend to actually rotate
      // (refresh window is REFRESH_THRESHOLD; ~1 hour). Asking earlier
      // is a no-op on the server (returns the current token).
      if (msUntilExpiry < 10 * 60 * 1000 && msUntilExpiry > 0) {
        authService
          .refreshVSNToken()
          .then((result) => {
            if (result && 'token' in result && result.token) {
              const r: any = result;
              // Map the wire user and carry channelIds forward — refresh omits
              // them, and storing raw would blank a ChannelAdmin's `type`.
              const prevUser = useAuthStore.getState().user;
              const user = toSessionUser(r.user, prevUser);
              useAuthStore.getState().setVSNAuth(r.token, r.expiresAt, user, r.absoluteExpiresAt);
            }
          })
          .catch(() => undefined);
      }
    }, REFRESH_TICK_MS);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [isAuthenticated]);
}
